import React from 'react';
import Seo from '@/components/Seo';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import CoachMap from '@/components/CoachMap';
import LegalFooter from '@/components/LegalFooter';
import { ChevronLeft } from 'lucide-react';
import { useAuth } from '@/lib/auth.jsx';

/**
 * Find an Instructor — public discovery page.
 *
 * Reachable from:
 *   - The parent dashboard sidebar (pinned CTA at the bottom)
 *   - Public navigation / direct URL share
 *
 * Renders the existing CoachMap component (same one used on the marketing
 * landing page) with a parent-focused header and a return-link to wherever
 * the parent came from. Accessible logged-in OR logged-out so parents can
 * still browse if they've signed out, or share the link with a friend.
 */
export default function FindInstructor() {
    const { user } = useAuth();
    const backHref = user?.role === 'parent' ? '/parent/dashboard' : '/';

    return (
        <div className="min-h-screen bg-[#f4f7fb]">
            <Seo
                title="Find a Swim Instructor Near You | SplashPad"
                description="Browse the worldwide SplashPad coach directory. Find independent swim instructors near you, view their programs, and register for lessons online."
                canonical="https://splashpad.pro/find-instructor"
            />
            {/* Header — light, navigational. Mirrors the parent layout
                feel so the page doesn't feel "lost" when navigated to
                from inside the dashboard. */}
            <header className="border-b bg-white">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
                    <Link to="/"><Logo /></Link>
                    <Link to={backHref}>
                        <Button variant="ghost" size="sm" data-testid="find-instructor-back-button">
                            <ChevronLeft className="mr-1 h-4 w-4" />
                            {user?.role === 'parent' ? 'Back to dashboard' : 'Home'}
                        </Button>
                    </Link>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6" data-testid="find-instructor-page">
                <div className="mb-6">
                    <h1 className="font-display text-3xl font-bold text-[#0a1628]">Find an instructor</h1>
                    <p className="mt-1 max-w-2xl text-sm text-[#1a3a5c]/80">
                        Browse every SplashPad coach on the map. Click a pin to see their packages, location, and start an application.
                    </p>
                </div>
                {/* CoachMap is the same component the marketing landing
                    page uses. It already handles fetching, filtering by
                    map bounds, marker clustering, and popup CTAs — we
                    just drop it in here with the same surface area. */}
                <CoachMap />
            </main>

            <LegalFooter />
        </div>
    );
}
